if (!customElements.get('wlsn-header')) {
  class WLSNHeader extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'wlsn-header--hidden',
        sticky: 'wlsn-header--sticky',
        stickyScrollUp: 'wlsn-header--sticky-scroll-up',
        section: 'wlsn-header-section',
        headerMenuWrapper: 'js-header-menu-wrapper',
        lockScroll: 'wlsn-lock-scroll',
        mobileMenuToggle: 'js-mobile-menu-toggle',
        megaMenuOverlay: 'js-wlsn-megamenu-dropdown-overlay',
        wlsnAccordionElement: 'wlsn-accordion',
        stickyActiveClass: 'wlsn-header--sticky-active',
      };

      this.behavior = this.dataset.headerBehavior || '';
      this.lastScroll = window.scrollY;
      this.isMobileMenuOpen = false;
      this.stickyScrollThreshold = 100; // Minimum scroll distance before stickyActiveClass is added
      
      // Store bound methods for proper event listener cleanup
      this._boundHandleScroll = this._handleScroll.bind(this);
      this._boundHandleStickyActive = this._handleStickyActive.bind(this);
      this._boundOnEscKey = this._onEscKey.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        headerSection: document.querySelector(`.${this.classes.section}`),
        headerMenuWrapper: this.querySelector(`.${this.classes.headerMenuWrapper}`),
        mobileMenuToggles: this.querySelectorAll(`.${this.classes.mobileMenuToggle}`),
        megaMenuOverlay: this.querySelector(`.${this.classes.megaMenuOverlay}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const { megaMenuOverlay, mobileMenuToggles, headerSection } = this.elements;

      if (megaMenuOverlay) {
        megaMenuOverlay.addEventListener('click', this._onMegaMenuOverlayClick.bind(this));
      }

      if (window.innerWidth < 1023) {
        mobileMenuToggles.forEach((toggle) => {
          toggle.addEventListener('click', this._mobileMenuToggle.bind(this));
        });
      }

      if (headerSection) {
        headerSection.classList.remove(this.classes.sticky, this.classes.stickyScrollUp);
        this._handleHeaderBehavior();
      }
      this._setHeaderHeight();

      // ESC key handle for all modals
      document.addEventListener('keydown', this._boundOnEscKey);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      const { megaMenuOverlay, mobileMenuToggles, headerSection } = this.elements;

      if (megaMenuOverlay) {
        megaMenuOverlay.removeEventListener('click', this._onMegaMenuOverlayClick.bind(this));
      }

      if (window.innerWidth < 1023) {
        mobileMenuToggles.forEach((toggle) => {
          toggle.removeEventListener('click', this._mobileMenuToggle.bind(this));
        });
      }

      if (headerSection) {
        headerSection.classList.remove(this.classes.sticky, this.classes.stickyScrollUp, this.classes.stickyActiveClass);
      }

      if (this.behavior === 'sticky_scroll_up' || this.behavior === 'always_sticky') {
        window.removeEventListener('scroll', this._boundHandleStickyActive);
      }
      if (this.behavior === 'sticky_scroll_up') {
        window.removeEventListener('scroll', this._boundHandleScroll);
      }
      document.removeEventListener('keydown', this._boundOnEscKey);
    }

    /**
     * Reset accordion state by closing all accordions
     * @returns {void}
     */
    _resetAccordionState() {
      const { headerMenuWrapper } = this.elements;
      if (headerMenuWrapper) {
        headerMenuWrapper.querySelectorAll(`${this.classes.wlsnAccordionElement}`).forEach((acc) => {
          acc.dispatchEvent(new CustomEvent('wlsn:accordion:close-all', { bubbles: false }));
        });
      }
    }

    /**
     * Handle mega menu overlay click event
     * @returns {void}
     */
    _onMegaMenuOverlayClick() {
      const { headerMenuWrapper } = this.elements;
      if (headerMenuWrapper?.classList.contains(this.classes.active)) {
        this._resetMobileMenuState();
      }
    }

    /**
     * Toggle mobile menu open/closed
     * @returns {void}
     */
    _mobileMenuToggle() {
      const { headerMenuWrapper, megaMenuOverlay } = this.elements;
      if (headerMenuWrapper?.classList.contains(this.classes.active)) {
        this._resetMobileMenuState();
      } else {
        headerMenuWrapper?.classList.add(this.classes.active);
        megaMenuOverlay?.classList.add(this.classes.active);
        this._lockScroll();
        this._updateOverlayState();
        this.isMobileMenuOpen = true;
      }
    }

    /**
     * Update overlay state based on menu state
     * @returns {void}
     */
    _updateOverlayState() {
      const { megaMenuOverlay, headerMenuWrapper, mobileMenuToggles } = this.elements;
      const isOpen = Boolean(headerMenuWrapper?.classList.contains(this.classes.active));
      if (megaMenuOverlay) {
        megaMenuOverlay.classList.toggle(this.classes.active, isOpen);
      }
      mobileMenuToggles.forEach((toggle) => {
        toggle.classList.toggle(this.classes.active, isOpen);
      });
    }

    /**
     * Reset mobile menu to closed state
     * @returns {void}
     */
    _resetMobileMenuState() {
      const { headerMenuWrapper, megaMenuOverlay } = this.elements;
      headerMenuWrapper?.classList.remove(this.classes.active);
      megaMenuOverlay?.classList.remove(this.classes.active);
      this._unlockScroll();
      this._resetAccordionState();
      this._updateOverlayState();
      this.isMobileMenuOpen = false;
    }

    /**
     * Handle ESC key press
     * @param {KeyboardEvent} e - The keyboard event
     * @returns {void}
     */
    _onEscKey(e) {
      if (e.key === 'Escape') {
        if (this.isMobileMenuOpen) {
          this._resetMobileMenuState();
        }
      }
    }

    /**
     * Handle header behavior based on settings
     * @returns {void}
     */
    _handleHeaderBehavior() {
      const { headerSection } = this.elements;
      if (this.behavior === 'always_sticky') {
        headerSection?.classList.add(this.classes.sticky);
        window.addEventListener('scroll', this._boundHandleStickyActive);
        // Check initial scroll position
        this._handleStickyActive();
      } else if (this.behavior === 'sticky_scroll_up') {
        headerSection?.classList.add(this.classes.stickyScrollUp);
        window.addEventListener('scroll', this._boundHandleScroll);
        window.addEventListener('scroll', this._boundHandleStickyActive);
        // Check initial scroll position
        this._handleStickyActive();
      }
    }

    /**
     * Handle scroll event for sticky header
     * @returns {void}
     */
    _handleScroll() {
      const { headerSection } = this.elements;
      const currentScroll = window.scrollY;
      if (!headerSection) return;
      headerSection.classList.toggle(
        this.classes.hidden,
        currentScroll > this.lastScroll && currentScroll > 20
      );
      this.lastScroll = currentScroll;
    }

    /**
     * Handle sticky active class based on scroll position with threshold
     * @returns {void}
     */
    _handleStickyActive() {
      const { headerSection } = this.elements;
      if (!headerSection) return;
      
      const currentScroll = window.scrollY;
      const shouldBeActive = currentScroll > this.stickyScrollThreshold;
      
      headerSection.classList.toggle(this.classes.stickyActiveClass, shouldBeActive);
    }

    /**
     * Set header height CSS variable
     * @returns {void}
     */
    _setHeaderHeight() {
      const headerHeight = this.offsetHeight;
      document.documentElement.style.setProperty('--wlsn-header-height', `${headerHeight / 10}rem`);
    }

    /**
     * Lock body scroll
     * @returns {void}
     */
    _lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    /**
     * Unlock body scroll
     * @returns {void}
     */
    _unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }
  }
  customElements.define('wlsn-header', WLSNHeader);
}
